/* eslint-disable no-useless-escape */
/* eslint-disable no-unused-vars */
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { formatMessage } from 'i18n/CurrentLocale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faExclamationTriangle, faInfoCircle, faPlug, faSpinner, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { COUNTRY_CODE, ModalEnum } from 'myParkdUtils/enums';
import { checkIfCompanyHasAppAccount, isAllowedKeyCodeForLicensePlateImport, stripStringFromNonAlphaNumericCharactersAllowSeparators } from 'myParkdUtils/utils';
import * as importVehiclesActions from 'features/vehicles/importVehiclesActions';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as companyActions from 'features/company/companyActions';
import * as telematicAccountsActions from 'features/telematics/telematicAccountsActions';
import NoAppAccountMessage from 'screens/vehicles-import/NoAppAccountMessage';
import ReactFlagsSelect from 'react-flags-select';
import ImportRequestValidationResult from 'screens/vehicles-import/ImportRequestValidationResult';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import AppWrapper from 'components/board/AppWrapper';
import countries from 'world-countries';
import { AlertBox } from 'components/AlertBox';
import MainHeader from 'components/board/MainHeader';
import MainContent from 'components/board/MainContent';
import { ReduxState } from 'types';
import { TELEMATIC_TYPE_APP } from "../../myParkdUtils/constants";


export function splitStringIntoLicensePlates(string: string) {
    if (!string.length || !string.trim().length) {
        return [];
    }
    return string.match(/[^\r\n\t\f\v\, ]+/g);
}

export function addCountryCodeAndTelematicId(licensePlates: string[], countryCode: string) {
    return licensePlates.map((licensePlate) => ({
        licensePlate: licensePlate,
        telematicId: licensePlate,
        countryCode: countryCode,
    }));
}

//=====================================================================================

const ImportLicensePlatesScreen = (props: Props) => {
    const navigate = useNavigate();
    const textAreaLP = useRef<HTMLTextAreaElement>(null);

    const [licensePlatesInput, setLicensePlatesInput] = useState('');
    const [licensePlates, setLicensePlates] = useState([]);
    const [cursorSelection, setCursorSelection] = useState({ start: 0, end: 0 });

    const [licensePlatesCountryCode, setLicensePlatesCountryCode] = useState(props.companyCountryCode || COUNTRY_CODE.NL);
    const [telematicAccountType, setTelematicAccountType] = useState(TELEMATIC_TYPE_APP);
    const [companyHasAppAccount, setCompanyHasAppAccount] = useState(true);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const companyTelematicAccounts = props.companyTelematicAccounts;
    const validationResult = props.validationResult;
    const importingStatus = props.importingStatus;

    const countryCodes = countries.map((country) => country.cca2);

    useEffect(() => {
        props.getCompanyTelematicAccounts();
        props.getCompanyDetails();
    }, []);

    useEffect(() => {
        if (companyTelematicAccounts.succeeded) {
            const companyHasAppAccount = checkIfCompanyHasAppAccount(companyTelematicAccounts.accounts);
            setCompanyHasAppAccount(companyHasAppAccount);
        }
    }, [companyTelematicAccounts]);

    useEffect(() => {
        if (validationResult.valid) {
            importValidLicensePlates();
        }
    }, [validationResult]);

    useEffect(() => {
        if (importingStatus.importSucceeded) {
            props.updateModal(ModalEnum.IMPORT_LICENSE_PLATES_SUCCEEDED, {
                invalidImportRequests: validationResult.invalidImportRequests,
                validImportRequests: validationResult.validImportRequests,
            });
            props.resetImportState();
            navigate('/vehicles');
        }
    }, [importingStatus]);

    useEffect(() => {
        countLicensePlates();
        updateCursorSelection();
    }, [licensePlatesInput]);

    function handleKeyPress(e) {
        if (!isAllowedKeyCodeForLicensePlateImport(e.keyCode)) {
            e.preventDefault();
        }
    }

    function insertPastedDataAtCursorPosition(e, filteredPastedData) {
        const cursorSelectionStart = e.target.selectionStart;
        const cursorSelectionEnd = e.target.selectionEnd;
        const partBeforeCursor = licensePlatesInput.toString().slice(0, cursorSelectionStart);
        const partAfterCursor = licensePlatesInput.toString().slice(cursorSelectionEnd);
        setCursorSelection({
            start: cursorSelectionStart + filteredPastedData.length,
            end: cursorSelectionEnd + filteredPastedData.length,
        });
        setLicensePlatesInput(`${partBeforeCursor}${filteredPastedData}${partAfterCursor}`);
    }

    function updateCursorSelection() {
        if (!textAreaLP.current) return;
        textAreaLP.current.selectionStart = cursorSelection.start;
        textAreaLP.current.selectionEnd = cursorSelection.end;
    }

    function handleOnPaste(e: any) {
        const clipboardData = e.clipboardData || window.clipboardData; // TODO: DOES NOT WORK IN IE11??? write workarround if(IE11){alternativeHandleOnPaste...}?
        const pastedData = clipboardData.getData('Text');
        const filteredPastedData = stripStringFromNonAlphaNumericCharactersAllowSeparators(pastedData);
        e.preventDefault();
        insertPastedDataAtCursorPosition(e, filteredPastedData);
    }

    function handleOnChange(e) {
        setLicensePlatesInput(e.target.value.toUpperCase());
        setCursorSelection({
            start: e.target.selectionStart,
            end: e.target.selectionEnd,
        });
    }

    function handleCountryChange(countryCode: string) {
        setLicensePlatesCountryCode(countryCode);
    }

    function countLicensePlates() {
        const licensePlatesJSON = splitStringIntoLicensePlates(licensePlatesInput);
        setLicensePlates(licensePlatesJSON);
    }

    async function validateLicensePlates(e: FormEvent) {
        e.preventDefault();
        if (licensePlates.length) {
            try {
                const licensePlatesWithCountryCode = addCountryCodeAndTelematicId(licensePlates, licensePlatesCountryCode);
                props.validateEnteredLicensePlates(telematicAccountType, licensePlatesWithCountryCode);
            } catch (err) {
                console.log(err);
            }
        } else {
            setShowValidationErrors(true);
        }
    }

    function importValidLicensePlates() {
        const proposedDbActions = props.validationResult.proposedDbActions;
        const fullyValid = props.validationResult.valid;
        if (proposedDbActions.length) {
            props.importValidLicensePlates(telematicAccountType, proposedDbActions, fullyValid);
        }
    }

    return (
        <AppWrapper>
            <main id="page-import-licenseplates" className="col-main">

                <MainHeader>
                    <div className="header-v-align">
                        <h2 className="main-header-title">
                            <FormattedMessage id="IMPORT_LICENSEPLATES" />
                        </h2>
                    </div>
                </MainHeader>

                <MainContent>
                    <GlobalErrorBoundary className="eb-red-background eb-rounded-corners">
                        <div className="board-content radius-rectangle pad-20">
                            {companyTelematicAccounts.error ? (
                                <div className="error-box">
                                    <FormattedMessage id="ERROR_SOMETHING_WENT_WRONG" />
                                </div>
                            ) : (
                                <React.Fragment>
                                    {companyHasAppAccount ? (
                                        <form
                                            onSubmit={validateLicensePlates}
                                            className={showValidationErrors ? 'show-validation-messages' : ''}
                                        >
                                            <AlertBox type='warn' icon={faPlug} style={{ marginBottom: '10px' }}>
                                                <FormattedMessage id="IMPORT_LICENSEPLATES_WARNING_TRACKED_VEHICLES" />
                                            </AlertBox>

                                            <p>
                                                1. <FormattedMessage id="CHOOSE_COUNTRY_FOR_WHICH_LICENSEPLATES_ARE_REGISTERED" />
                                            </p>
                                            <div className='width-third'>
                                                <ReactFlagsSelect
                                                    className="myparkd-react-flags-select"
                                                    countries={countryCodes}
                                                    searchable={true}
                                                    onSelect={handleCountryChange}
                                                    selected={licensePlatesCountryCode}
                                                    searchPlaceholder={formatMessage({ id: 'PLACEHOLDER_SEARCH' })}
                                                />
                                            </div>

                                            <label htmlFor="enter-license-plates-field">
                                                2. <FormattedMessage id="ENTER_LICENSEPLATES" />
                                            </label>

                                            <AlertBox type='info' icon={faInfoCircle} style={{ marginBottom: '10px' }}>
                                                <FormattedMessage id="IMPORT_LICENSEPLATES_INSTRUCTIONS" />
                                            </AlertBox>

                                            <textarea
                                                ref={textAreaLP}
                                                id="enter-license-plates-field"
                                                className={'text-field' + (licensePlates?.length ? ' valid' : ' error')}
                                                placeholder={formatMessage({ id: 'PLACEHOLDER_IMPORT_LICENSEPLATES_TEXTAREA' })}
                                                value={licensePlatesInput}
                                                onKeyDown={handleKeyPress}
                                                onPaste={handleOnPaste}
                                                onChange={handleOnChange}
                                                autoFocus
                                            />

                                            {showValidationErrors && !licensePlates.length && (
                                                <p className="red-box">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="fa-icon" />
                                                    <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />
                                                </p>
                                            )}

                                            {!!licensePlates.length && (
                                                <p className="float-left">
                                                    <FontAwesomeIcon icon={faCar} className="fa-icon" />
                                                    <FormattedMessage
                                                        id="X_LICENSEPLATES_FOUND"
                                                        values={{ numberOfLicensePlates: licensePlates.length }}
                                                    />
                                                </p>
                                            )}

                                            <div className="align-right">
                                                <button type="button" className="btn-blue" onClick={validateLicensePlates}>
                                                    {props.validatingStatus.validating ? (
                                                        <React.Fragment>
                                                            <FontAwesomeIcon icon={faSpinner} className="fa-icon rotating-spinner" />
                                                            <FormattedMessage id="VALIDATING_LICENSE_PLATES" />
                                                        </React.Fragment>
                                                    ) : (
                                                        <FormattedMessage id="VALIDATE_LICENSE_PLATES" />
                                                    )}
                                                </button>
                                            </div>

                                            {(props.validatingStatus.validationError || props.importingStatus.importError) && (
                                                <p className="red-box api-error">
                                                    <FontAwesomeIcon icon={faTimesCircle} className="fa-icon" />
                                                    <FormattedMessage id="ERROR_SOMETHING_WENT_WRONG" />
                                                </p>
                                            )}
                                        </form>
                                    ) : (
                                        <NoAppAccountMessage />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </GlobalErrorBoundary>

                    {!!(!props.validationResult.valid && props.validationResult.requests.length) && (
                        <ImportRequestValidationResult importValidLicensePlates={importValidLicensePlates} />
                    )}
                </MainContent>

            </main>
        </AppWrapper>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        companyTelematicAccounts: state.companyTelematicAccounts,
        companyCountryCode: state.companyDetails.companyDetails.address.countryCode,
        validationResult: state.licensePlatesImports.validationResult,
        validatingStatus: state.licensePlatesImports.validatingStatus,
        importingStatus: state.licensePlatesImports.importingStatus,
    };
}

function mapDispatchToProps(dispatch: any) {
    return bindActionCreators(
        {
            getCompanyDetails: companyActions.getCompanyDetails,
            getCompanyTelematicAccounts: telematicAccountsActions.getCompanyTelematicAccounts,
            validateEnteredLicensePlates: importVehiclesActions.validateEnteredLicensePlates,
            importValidLicensePlates: importVehiclesActions.importValidLicensePlates,
            resetImportState: importVehiclesActions.resetImportState,
            updateModal: modalWindowActions.updateModalWindow,
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ImportLicensePlatesScreen);